// 스페셜 몬스터 알림 띠(위쪽 가장자리) 문구·자리 규칙(순수 모듈 — DOM은 specialBand.ts).
// 「스페셜 몬스터 출현! 황금 고블린」 + 남은 초 / 「챔피언 출현! 신속 이끼 고블린」 / 처치 뒤 「황금 고블린 처치! +500 골드 · 강화석 ×2」.
// 플레이 영역(영웅·앞길·위험물)을 가리지 않게 위쪽 왼편(영웅 체력)·오른편(지역 이름·미니맵) HUD 사이 빈 띠에만 놓는다.
import { CHAMPION_AFFIXES, MobSpecial } from '../shared/specialMonster';
import type { SpecialView } from '../shared/types';

export interface SpecialBandModel {
  readonly tone: 'gold' | 'champion' | 'reward';
  /** 앞 문구('스페셜 몬스터 출현!') */
  readonly head: string;
  /** 강조 이름 */
  readonly name: string;
  /** 오른쪽 칸(남은 초 '12초' 또는 보상 요약) */
  readonly tail: string;
  /** 한 줄 설명(황금 고블린 도망 안내·챔피언 접두어 효과) — 넓을 때만 */
  readonly tip: string;
}

/** 보상 띠를 보이는 시간(ms) */
export const REWARD_BAND_MS = 2800;
/** 띠 최대 폭(px, 게임 화면 좌표) */
export const SPECIAL_BAND_MAX = 520;
/** 띠와 HUD 사이 여백(px) */
export const SPECIAL_BAND_GAP = 8;
/** 이 폭 미만이면 앞 문구를 접고 이름·시간만 */
export const SPECIAL_BAND_COMPACT = 300;
/** 아래 줄에 놓을 때 띠 한 줄 높이 추정(px, 장애물 겹침 판정용)과 최소 폭 */
export const SPECIAL_BAND_ROW = 30;
export const SPECIAL_BAND_BELOW_MIN = 120;
/** 이 폭 미만이면 위쪽 빈 띠에 들어가지 않는다(자리를 오른쪽 HUD 아래로 옮긴다) */
export const SPECIAL_BAND_MIN = 150;

/** 지금 보일 띠(없으면 null). rewardAgeMs: 마지막 보상 번호가 바뀐 뒤 흐른 시간(없으면 큰 값) */
export function specialBandModel(v: SpecialView | undefined, rewardAgeMs: number, rewardMs: number = REWARD_BAND_MS): SpecialBandModel | null {
  if (!v) return null;
  if (v.rewardSerial > 0 && rewardAgeMs < rewardMs && v.phase === 2) {
    const gold = v.kind === MobSpecial.GoldGoblin;
    const parts: string[] = ['+' + v.rewardGold + ' 골드'];
    if (v.rewardStones > 0) parts.push('강화석 ×' + v.rewardStones);
    if (!gold) parts.push('장비 확정');
    return { tone: 'reward', head: gold ? '황금 고블린 처치!' : '챔피언 처치!', name: '', tail: parts.join(' · '), tip: '' };
  }
  if (v.phase !== 1) return null;
  if (v.kind === MobSpecial.GoldGoblin) {
    return { tone: 'gold', head: '스페셜 몬스터 출현!', name: v.name, tail: Math.max(0, Math.ceil(v.remainTicks / 60)) + '초', tip: v.fleeing ? '도망 중!' : '맞히면 도망친다' };
  }
  if (v.kind === MobSpecial.Champion && v.bannerTicks > 0) {
    return { tone: 'champion', head: '챔피언 출현!', name: v.name, tail: '', tip: CHAMPION_AFFIXES[v.affix]?.tip ?? '' };
  }
  return null;
}

export interface Box { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number }
export interface SpecialBandPlace {
  /** 'top' = 위쪽 HUD 사이 빈 띠, 'below' = 오른쪽 HUD 묶음 아래(빈 띠가 너무 좁을 때) */
  readonly mode: 'top' | 'below';
  readonly centerX: number;
  readonly top: number;
  readonly maxWidth: number;
  readonly compact: boolean;
}

/**
 * 자리: tl(위쪽 왼편)·tr(위쪽 오른편) HUD 사이 빈 띠 가운데(폭 ≥ MIN). 좁으면 오른쪽 HUD 묶음 아래 오른쪽 맞춤.
 * hint: 연계 안내 줄 상자(떠 있을 때). W: 게임 화면 폭. HUD가 아직 없으면(null) 화면 위 가운데.
 */
export function specialBandPlace(W: number, tl: Box | null, tr: Box | null, hint: Box | null = null, obstacles: readonly Box[] = []): SpecialBandPlace {
  const lo = tl ? tl.right : 0, hi = tr ? tr.left : W;
  const avail = Math.max(0, hi - lo - SPECIAL_BAND_GAP * 2);
  if (avail >= SPECIAL_BAND_MIN) {
    const w = Math.min(SPECIAL_BAND_MAX, avail);
    const cx = Math.round((lo + hi) / 2);
    // 연계 안내 줄(위쪽 가운데 한 줄 튜토리얼)이 떠 있고 가로로 겹치면 그 바로 아래 줄에 둔다(둘 다 읽히게)
    const under = hint && hint.left < cx + w / 2 && hint.right > cx - w / 2 ? Math.round(hint.bottom) + 2 : 4;
    return { mode: 'top', centerX: cx, top: under, maxWidth: w, compact: w < SPECIAL_BAND_COMPACT };
  }
  // 오른쪽 HUD 묶음 아래 줄: 같은 줄에 걸치는 장애물(목표 패널의 「자동」 칩·일시정지 등)의 오른쪽 가장자리 뒤에서 시작한다
  const top = (tr ? tr.bottom : 0) + 6, right = tr ? tr.right : W - SPECIAL_BAND_GAP;
  let left = SPECIAL_BAND_GAP;
  for (const o of obstacles) if (o.top < top + SPECIAL_BAND_ROW && o.bottom > top && o.right < right - SPECIAL_BAND_BELOW_MIN) left = Math.max(left, o.right + SPECIAL_BAND_GAP);
  const w = Math.min(SPECIAL_BAND_MAX, Math.max(SPECIAL_BAND_BELOW_MIN, Math.round(right - left)));
  return { mode: 'below', centerX: Math.round(right - w / 2), top, maxWidth: w, compact: true };
}

/** 레이아웃(HUD 상자) 재측정 최소 간격(ms). getBoundingClientRect는 강제 레이아웃이라 매 프레임·6프레임마다 읽지 않는다 */
export const SPECIAL_BAND_REMEASURE_MS = 500;

/**
 * 변화 감지(프레임 루프 무할당): 알림 띠가 문구 모델·DOM을 다시 만들어야 하는지, HUD 상자를 다시 재야 하는지.
 * 입력은 원시값 비교뿐이라 아무것도 새로 만들지 않는다. contentChanged = (출현 번호·단계·종류·접두어·도망·표시 초·챔피언 배너·보상 번호·보상 띠 표시)
 * 중 하나라도 바뀜, layoutDue = (배율·뷰포트가 바뀜) 또는 마지막 측정 뒤 SPECIAL_BAND_REMEASURE_MS 경과.
 */
export class SpecialBandGate {
  private on = false;
  private phase = -1; private kind = -1; private serial = -1; private affix = -1; private rewardSerial = -1; private rewardGold = -1; private rewardStones = -1;
  private fleeing = false; private secs = -1; private banner = false; private rewardOn = false;
  private u = -1; private w = -1; private h = -1; private measuredAt = -1e12;

  /** 던전이 아니거나 SpecialView가 없으면 scene=false. rewardOn = 처치 보상 띠를 보일 시간 안인지(호출 쪽이 계산) */
  contentChanged(v: SpecialView | undefined, inDungeon: boolean, rewardOn: boolean): boolean {
    const on = inDungeon && !!v;
    const phase = on ? v!.phase : -1, kind = on ? v!.kind : -1, serial = on ? v!.serial : -1, affix = on ? v!.affix : -1;
    const fleeing = on ? v!.fleeing : false, banner = on ? v!.bannerTicks > 0 : false;
    // 황금 고블린 남은 시간은 초 단위(올림)로만 센다 — 문구가 바뀌는 단위와 같다
    const secs = on && v!.phase === 1 ? Math.max(0, Math.ceil(v!.remainTicks / 60)) : -1;
    const rs = on ? v!.rewardSerial : -1, rg = on ? v!.rewardGold : -1, rst = on ? v!.rewardStones : -1;
    const ro = on && rewardOn;
    if (on === this.on && phase === this.phase && kind === this.kind && serial === this.serial && affix === this.affix && fleeing === this.fleeing
      && banner === this.banner && secs === this.secs && rs === this.rewardSerial && rg === this.rewardGold && rst === this.rewardStones && ro === this.rewardOn) return false;
    this.on = on; this.phase = phase; this.kind = kind; this.serial = serial; this.affix = affix; this.fleeing = fleeing; this.banner = banner;
    this.secs = secs; this.rewardSerial = rs; this.rewardGold = rg; this.rewardStones = rst; this.rewardOn = ro;
    return true;
  }

  /** 띠가 보이는 동안 HUD 상자를 다시 재야 하는지(재면 true를 돌려주고 기준 시각을 갱신한다) */
  layoutDue(u: number, w: number, h: number, nowMs: number): boolean {
    if (u === this.u && w === this.w && h === this.h && nowMs - this.measuredAt < SPECIAL_BAND_REMEASURE_MS && nowMs >= this.measuredAt) return false;
    this.u = u; this.w = w; this.h = h; this.measuredAt = nowMs;
    return true;
  }

  /** 띠가 꺼졌다: 다음에 켜질 때 바로 다시 잰다 */
  forgetLayout(): void { this.measuredAt = -1e12; }
}
